import { useState, type FormEvent } from 'react';
import { useCurrentUserId, useReplyMutation, useSupportTicketDetail, useUpdateStatusMutation } from '../hooks/useSupportTickets';
import SupportTicketCloseModal from './SupportTicketCloseModal';
import type { SupportTicketTmMessage } from '@policy-tracker/types';

/**
 * Detail view for a single TM support ticket (#2304 part 2).
 *
 * Renders the message thread with author labels that follow this rule
 * (anti-impersonation):
 *
 *   - `authorSide: 'tenant'`            → message.authorName (the tenant
 *                                          admin's display name)
 *   - `authorSide: 'tm' && self`        → "You"
 *   - `authorSide: 'tm' && other`       → message.authorName (resolved by
 *                                          the TM API via AdminGetUser) or
 *                                          the "Accedo Support" sentinel
 *                                          when name resolution failed.
 *
 * The reply box is disabled when the ticket is closed. Close opens the
 * `SupportTicketCloseModal` to capture `closedReason`. Reopen prompts via
 * `window.confirm` then PATCHes `{ status: 'open' }`.
 */
interface Props {
    tenantId: string;
    ticketId: string;
    onBack: () => void;
}

function authorLabelFor(message: SupportTicketTmMessage, currentUserId: string | null): string {
    if (message.authorSide === 'tenant') {
        return message.authorName ?? 'Tenant admin';
    }
    if (currentUserId && message.authorUserId === currentUserId) return 'You';
    return message.authorName ?? 'Accedo Support';
}

function formatTimestamp(iso: string): string {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function SupportTicketDetail({ tenantId, ticketId, onBack }: Props) {
    const ticketQuery = useSupportTicketDetail(tenantId, ticketId);
    const reply = useReplyMutation();
    const updateStatus = useUpdateStatusMutation();
    const currentUserId = useCurrentUserId();

    const [replyBody, setReplyBody] = useState('');
    const [showCloseModal, setShowCloseModal] = useState(false);

    if (ticketQuery.isLoading) return <p>Loading ticket...</p>;
    if (ticketQuery.error) return <p>Error loading ticket.</p>;
    const ticket = ticketQuery.data;
    if (!ticket) return <p>Ticket not found.</p>;

    const isClosed = ticket.status === 'closed';

    const handleSubmitReply = (e: FormEvent) => {
        e.preventDefault();
        const body = replyBody.trim();
        if (!body) return;
        reply.mutate(
            { tenantId, ticketId, body },
            {
                onSuccess: () => setReplyBody(''),
            }
        );
    };

    const handleConfirmClose = (closedReason: string) => {
        updateStatus.mutate({ tenantId, ticketId, body: { status: 'closed', closedReason } });
        setShowCloseModal(false);
    };

    const handleReopen = () => {
        if (!window.confirm('Reopen this ticket?')) return;
        updateStatus.mutate({ tenantId, ticketId, body: { status: 'open' } });
    };

    return (
        <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                    <button className="btn btn-outline" onClick={onBack}>
                        &larr; Back
                    </button>
                </div>
                <div>
                    {!isClosed && (
                        <button className="btn" onClick={() => setShowCloseModal(true)}>
                            Close ticket
                        </button>
                    )}
                    {isClosed && (
                        <button className="btn" onClick={handleReopen}>
                            Reopen
                        </button>
                    )}
                </div>
            </div>

            <h2 style={{ marginTop: 0 }}>{ticket.subject}</h2>
            <p>
                Tenant: <strong>{ticket.tenantName}</strong>
                {' — Status: '}
                <strong>{ticket.status}</strong>
                {ticket.status === 'closed' && ticket.closedReason && (
                    <>
                        {' — Reason: '}
                        <em>{ticket.closedReason}</em>
                    </>
                )}
            </p>

            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '0.5rem',
                    padding: '0.5rem',
                    marginBottom: '1rem',
                    maxHeight: '32rem',
                    overflowY: 'auto',
                }}
            >
                {ticket.messages.map((m) => (
                    <div
                        key={m.messageId}
                        style={{
                            marginBottom: '0.75rem',
                            padding: '0.5rem',
                            background: m.authorSide === 'tm' ? '#eff6ff' : '#f9fafb',
                            borderRadius: '0.375rem',
                        }}
                    >
                        <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                            <strong>{authorLabelFor(m, currentUserId)}</strong>
                            <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>{formatTimestamp(m.createdAt)}</span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                    </div>
                ))}
            </div>

            <form onSubmit={handleSubmitReply}>
                <label htmlFor="reply">Reply</label>
                <textarea
                    id="reply"
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    disabled={isClosed || reply.isPending}
                    rows={4}
                    maxLength={4000}
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                />
                <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                    <button type="submit" className="btn" disabled={isClosed || reply.isPending || !replyBody.trim()}>
                        Send
                    </button>
                </div>
            </form>

            {showCloseModal && <SupportTicketCloseModal onSubmit={handleConfirmClose} onCancel={() => setShowCloseModal(false)} />}
        </div>
    );
}
